import { useState } from 'react';

import { cn } from '@/lib/css-utils';

import { DatePicker } from '../date-picker';
import { MonthPicker } from '../month-picker';
import { RadioButtonGroup, RadioButtonGroupItem } from '../radio-button-group';
import { Select, SelectOption } from '../select';
import { Checkbox } from './checkbox';
import { FieldRenderer } from './form-controller';
import { Input, InputProps } from './input';
import { TextArea } from './textarea';

export const renderTextInput: FieldRenderer<number | string> = ({ extraProps, ...rest }, ref) => {
    const finalProps = {
        ...rest,
        ...extraProps,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            rest.onChange?.(e);
            extraProps?.onChange?.(e.target.value);
        },
    };

    return <Input {...finalProps} ref={ref} />;
};

// renderMultiTextInput handles fields marked as `multiple` that don't have a fixed choice list
// (e.g. free-form multi-value fields like a list of country codes). The value is kept as an
// array in form state, and displayed/edited as a comma-separated string.
export const renderMultiTextInput: FieldRenderer<string[]> = ({ value, extraProps, onChange, ...rest }, ref) => {
    const displayValue = Array.isArray(value) ? value.join(', ') : (value ?? '');

    const finalProps = {
        ...rest,
        ...extraProps,
        value: displayValue,
        placeholder: rest.placeholder ?? extraProps?.placeholder ?? 'Comma-separated values',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const parsed = e.target.value
                .split(',')
                .map((item) => item.trim())
                .filter((item) => item.length > 0);

            onChange?.(parsed);
            extraProps?.onChange?.(parsed);
        },
    };

    return <Input {...finalProps} ref={ref} />;
};

export function renderSelectBox<T extends Record<string, any>, VT = string>(
    items: SelectOption<T, VT>[],
    extraProps?: Record<string, any>
) {
    const Renderer: FieldRenderer = (
        { value, onChange, label, placeholder, disabled, hasError, loading, ...rest },
        ref
    ) => {
        const isDisabled = disabled || typeof loading === 'boolean' ? loading : false;

        const finalProps = {
            disabled: isDisabled,
            loading,
            placeholder,
            label,
            ...extraProps,
            ...rest,
        };

        return (
            <Select
                {...finalProps}
                loadingAnimation="pulse"
                color="grey"
                mode="dark"
                options={items}
                onChange={onChange}
                value={value || null}
                hasError={hasError}
                size="lg"
                selectRef={ref}
            />
        );
    };

    return Renderer;
}

export const renderNumberInputFactory =
    (baseProps?: Partial<InputProps>): FieldRenderer<number | string> =>
    ({ extraProps, ...rest }, ref) => {
        const finalProps = { ...rest, ...extraProps };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value;
            const normalized = raw.replace(',', '.'); // Normalize comma to dot

            // Allow negative numbers with optional decimal (now always dot)
            if (/^-?\d*\.?\d*$/.test(normalized)) {
                const normalizedEvent = {
                    ...e,
                    target: {
                        ...e.target,
                        value: normalized,
                    },
                } as React.ChangeEvent<HTMLInputElement>;

                finalProps.onChange?.(normalizedEvent); // Call parent with normalized value
            }
        };

        return (
            <Input
                {...finalProps}
                ref={ref}
                type="text"
                inputMode="decimal"
                onChange={handleChange}
                {...baseProps}
                inputClassName={cn(
                    '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                    baseProps?.inputClassName
                )}
            />
        );
    };

export const renderNumberMobileInputFactory =
    (baseProps?: Partial<InputProps>): FieldRenderer<number | string> =>
    ({ extraProps, ...rest }, ref) => {
        const finalProps = { ...rest, ...extraProps };
        const [value, setValue] = useState('');

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const input = e.target.value.replace(',', '.'); // normalize comma
            if (/^-?\d*\.?\d*$/.test(input)) {
                setValue(input);
                finalProps.onChange?.({
                    ...e,
                    target: {
                        ...e.target,
                        value: input,
                    },
                } as React.ChangeEvent<HTMLInputElement>);
            }
        };

        const handleSignToggle = (sign: '+' | '-') => {
            if (!value) return;

            const isNegative = value.startsWith('-');
            const newValue =
                sign === '-' && !isNegative ? `-${value}` : sign === '+' && isNegative ? value.slice(1) : value;

            setValue(newValue);
            finalProps.onChange?.({
                target: {
                    ...((ref as React.RefObject<HTMLInputElement | null>)?.current || {}),
                    value: newValue,
                },
            } as unknown as React.ChangeEvent<HTMLInputElement>);
        };

        const currentSign = value.startsWith('-') ? '-' : '+';

        return (
            <div className="flex items-center gap-2 w-full">
                <RadioButtonGroup
                    value={currentSign}
                    onValueChange={(val) => handleSignToggle(val as '+' | '-')}
                    className="shrink-0 focus:outline-none focus-within:outline-none border-none"
                >
                    <RadioButtonGroupItem value="+">+</RadioButtonGroupItem>
                    <RadioButtonGroupItem value="-">-</RadioButtonGroupItem>
                </RadioButtonGroup>

                <Input
                    className="w-full"
                    {...finalProps}
                    ref={ref}
                    type="text"
                    inputMode="decimal"
                    value={value}
                    onChange={handleChange}
                    {...baseProps}
                    inputClassName={cn(
                        '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none w-full',
                        baseProps?.inputClassName
                    )}
                />
            </div>
        );
    };

export const renderDateInput: FieldRenderer<Date> = (
    { extraProps, value, onChange, disabled, hasError, ...rest },
    ref
) => {
    const finalProps = { ...rest, ...extraProps };

    return (
        <DatePicker
            {...finalProps}
            value={value}
            onChange={(date) => onChange({ target: { value: date } })}
            disabled={disabled}
            hasError={hasError}
            noInputGroup
            className={cn('min-w-80', finalProps.className)}
            // name={props.name} // TODO: This is not used in DatePicker
        />
    );
};

export const renderMonthPicker: FieldRenderer<Date> = (
    { extraProps, value, onChange, disabled, hasError, ...rest },
    ref
) => {
    const finalProps = { ...rest, ...extraProps };

    return (
        <MonthPicker
            {...finalProps}
            value={value}
            onChange={(date) => onChange({ target: { value: date } })}
            disabled={disabled}
            hasError={hasError}
            noInputGroup
            className={cn('w-full', finalProps.className)}
        />
    );
};

export const renderRadioButtonGroupFactory =
    (
        items: Array<{
            value: number | string;
            label: string | React.ReactNode;
        }>,
        anyOption = false,
        anyLabel = 'Any',
        baseProps: React.ComponentPropsWithoutRef<typeof RadioButtonGroup> = {}
    ): FieldRenderer =>
    ({ extraProps, value, onChange, disabled, name, hasError, loading, ...rest }, ref) => {
        const finalProps = { ...baseProps, ...rest, ...extraProps };

        const isDisabled = loading ? loading : disabled;

        return (
            <RadioButtonGroup
                {...finalProps}
                value={value ? `${value}` : ''}
                onValueChange={onChange}
                name={name}
                ref={ref}
                hasError={hasError}
                disabled={isDisabled}
                className={cn('inline-flex overflow-x-scroll no-scrollbar mx-1', finalProps.className)}
            >
                {anyOption && (
                    <RadioButtonGroupItem key="any" value="">
                        {anyLabel}
                    </RadioButtonGroupItem>
                )}

                {items.map((item) => (
                    <RadioButtonGroupItem key={item.value} value={`${item.value}`}>
                        {item.label}
                    </RadioButtonGroupItem>
                ))}
            </RadioButtonGroup>
        );
    };

export const renderCheckbox: FieldRenderer<boolean> = ({
    value,
    onChange,
    label,
    extraProps,
    hasError,
    placeholder,
    error,
    ...rest
}) => {
    const finalProps = { ...rest, ...extraProps };

    return (
        <Checkbox {...finalProps} checked={value} onCheckedChange={(e) => onChange(e)}>
            {label}
        </Checkbox>
    );
};

export const renderTextarea: FieldRenderer<number | string> = ({ extraProps, ...rest }, ref) => {
    const finalProps = { ...rest, ...extraProps };

    return <TextArea {...finalProps} ref={ref} />;
};
